import { FC } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { Link as CarbonLink } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { formatNumber } from 'utils/number';
import { missingSubtractBosLoadableAtom } from './atoms';
import type { MissingSubtractBos } from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

const columnHelper = createColumnHelper<MissingSubtractBos>();

const columns = [
  columnHelper.accessor('subtractName', {
    header: 'Subtract',
    size: 160,
  }),
  columnHelper.accessor('tractName', {
    header: 'Tract',
    size: 140,
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '--';
      return (
        <CarbonLink
          href={`/crm/tracts_and_subtracts/${info.row.original.tractId}/edit`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('instrumentType', {
    header: 'Instrument Type',
    size: 140,
  }),
  columnHelper.accessor('netAcreageUnits', {
    header: 'Net Acreage Units',
    size: 140,
  }),
  columnHelper.accessor('subtractAcres', {
    header: 'Subtract Acres',
    size: 130,
    cell: (info) => {
      const value = info.getValue();
      return value !== null && value !== undefined
        ? formatNumber({ value, minPrecision: 4, maxPrecision: 4 })
        : '--';
    },
  }),
  columnHelper.accessor('subtractGrossAcres', {
    header: 'Gross Acres',
    size: 120,
    cell: (info) => {
      const value = info.getValue();
      return value !== null && value !== undefined
        ? formatNumber({ value, minPrecision: 4, maxPrecision: 4 })
        : '--';
    },
  }),
  columnHelper.accessor('tractLeaseRoyaltyRate', {
    header: 'Royalty Rate',
    size: 120,
    cell: (info) => {
      const value = info.getValue();
      return value !== null && value !== undefined
        ? `${formatNumber({
            value: value * 100,
            minPrecision: 2,
            maxPrecision: 4,
          })}%`
        : '--';
    },
  }),
  columnHelper.accessor('tractBosUpdatedAt', {
    header: 'Tract BOS Updated',
    size: 160,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleDateString() : '--';
    },
  }),
];

export const MissingSubtractBosPage: FC = () => {
  const loadable = useAtomValue(missingSubtractBosLoadableAtom);

  const isLoading = loadable.state === 'loading';
  const data = loadable.state === 'hasData' ? loadable.data.results : [];
  const stats = loadable.state === 'hasData' ? loadable.data.stats : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Missing Subtract BOS"
        actions={null}
      />

      <div className={styles.sectionHeader}>
        <p>
          Subtracts missing Bundle of Sticks calculations where the parent tract
          has a BOS record. These subtracts should have inherited a BOS
          calculation but didn&apos;t.
        </p>
        <p className={styles.sectionDescription}>
          Expected: Empty &mdash; all subtracts with a BOS-bearing tract should
          have their own BOS record.
        </p>
      </div>

      {stats && (
        <StatsBar
          stats={[
            {
              label: 'Missing BOS',
              value: stats.totalMissing,
              tagType: 'red',
            },
            {
              label: 'Affected Tracts',
              value: stats.affectedTracts,
              tagType: 'magenta',
            },
          ]}
        />
      )}

      <DataTable
        data={data}
        columns={columns as ColumnDef<MissingSubtractBos>[]}
        getRowId={(row) => row.subtractId}
        isLoading={isLoading}
        {...TABLE_CONFIG}
      />
    </>
  );
};
